import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { ConsensusRevisionWidgetInstanceConfig, ConsensusRevisionData } from './types';
import {
  formatPrice,
  buildTimeAxisFormatter,
  SEMANTIC_CHART_COLORS,
} from '../../../design';
import type { AxisLabelFormatterParams } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { getSelectedKpi, subscribeKpi } from '../../../blocks/hooks/kpiSelectionStore';
import { ChartWidget, KeyMetricBadge } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';
import {
  varianceOverrideService,
  VARIANCE_OVERRIDES_CHANGED,
} from '../../../../../services/varianceOverrideService';
import type { VarianceOverride } from '../../../../../services/varianceOverrideService';

// Preview (no asset): the META test story — consensus revised up while the stock
// fell (correlation -0.78). Consensus is in $millions (InSync Scaler 10^6).
const SAMPLE_DATA: ConsensusRevisionData = {
  ticker: 'NVDA',
  kpi: 'Total Revenue',
  period: '2026',
  correlation: -0.78,
  series: [
    { date: '2025-12-18', consensus: 236178, price: 664.45 },
    { date: '2026-01-23', consensus: 236594, price: 658.76 },
    { date: '2026-02-28', consensus: 251885, price: 648.18 },
    { date: '2026-04-05', consensus: 251865, price: 574.46 },
    { date: '2026-05-11', consensus: 252969, price: 598.86 },
    { date: '2026-06-16', consensus: 253013, price: 593.48 },
  ],
};

interface Props {
  config: ConsensusRevisionWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

// Consensus arrives in $millions → scale to actual dollars for display.
const axisConsensus = (params: AxisLabelFormatterParams): string =>
  params.value == null ? '' : formatPrice(Number(params.value) * 1e6, 0);
const axisPrice = (params: AxisLabelFormatterParams): string =>
  params.value == null ? '' : `$${Number(params.value).toFixed(0)}`;

const ConsensusRevisionWidgetRenderer: React.FC<Props> = ({ config, dashboardData = {}, widgetInstanceId }) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  // The Variance table can drive which KPI this chart plots (cross-widget). When
  // the analyst picks a KPI there, this re-fetches + re-plots that KPI's revision.
  const selectedKpi = useSyncExternalStore(subscribeKpi, () => getSelectedKpi(uid));
  const fetchConfig = useMemo(() => {
    const c = config as unknown as Record<string, unknown>;
    return { ...c, kpi: selectedKpi ?? c.kpi ?? 'Total Revenue' };
  }, [config, selectedKpi]);

  const { data, loading, error, refetch } = useDataProviderWidget<ConsensusRevisionData>(
    'consensus_revision',
    uid,
    fetchConfig,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const ticker = displayData?.ticker || 'Asset';
  const kpi = displayData?.kpi || 'KPI';
  const period = displayData?.period || '';

  // The analyst's own estimate for this KPI's fiscal year (entered in the
  // Variance table) — overlaid as a dashed reference line so "my number vs
  // the street's trajectory" reads on one chart. The overrides service fires
  // VARIANCE_OVERRIDES_CHANGED on every save, so edits reflect live.
  const [overrides, setOverrides] = useState<VarianceOverride[]>([]);
  useEffect(() => {
    // Reset on asset change so the previous ticker's estimate can never
    // linger on the next ticker's chart while (or if) its fetch is pending.
    setOverrides([]);
    if (!uid) return;
    let alive = true;
    // Latest-wins: a multi-cell modal save fires one change event per PUT,
    // spawning overlapping GETs — only the newest request may apply.
    let seq = 0;
    const load = () => {
      const mine = ++seq;
      varianceOverrideService.list(uid)
        .then((rows) => { if (alive && mine === seq) setOverrides(rows); })
        .catch(() => {});
    };
    load();
    const onChange = (e: Event) => {
      const changed = (e as CustomEvent<{ ticker?: string }>).detail?.ticker;
      if (!changed || changed === uid) load();
    };
    window.addEventListener(VARIANCE_OVERRIDES_CHANGED, onChange);
    return () => { alive = false; window.removeEventListener(VARIANCE_OVERRIDES_CHANGED, onChange); };
  }, [uid]);

  // Chart period is the fiscal YEAR ("2026"); variance periods use "FY26".
  const internalEst = useMemo(() => {
    if (!period) return null;
    const fyLabel = `FY${String(period).slice(-2)}`;
    const row = overrides.find((o) => o.kpi === kpi && o.period === fyLabel);
    return row?.internal_value ?? null;
  }, [overrides, kpi, period]);

  const chartData = useMemo(() => {
    const rows = [...(displayData?.series ?? [])].sort((a, b) => a.date.localeCompare(b.date));
    return internalEst == null ? rows : rows.map((r) => ({ ...r, internal: internalEst }));
  }, [displayData, internalEst]);

  // Consensus on the left $ axis, stock price on the right $ axis (independent scales).
  const series = useMemo<LineChartSeries[]>(() => [
    { yKey: 'consensus', yName: `${kpi} Consensus`, axis: 'cons', color: SEMANTIC_CHART_COLORS.subject, markers: true },
    { yKey: 'price', yName: 'Stock Price', axis: 'price', color: SEMANTIC_CHART_COLORS.benchmark, markers: true },
    ...(internalEst != null
      ? [{ yKey: 'internal', yName: 'Your Estimate', axis: 'cons', color: SEMANTIC_CHART_COLORS.caution, markers: false, dashed: true } as LineChartSeries]
      : []),
  ], [kpi, internalEst]);

  const dates = useMemo(() => chartData.map((d) => d.date), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);

  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const last = chartData.length ? chartData[chartData.length - 1] : null;
    return series.map((s) => {
      const v = last ? (last as unknown as Record<string, unknown>)[s.yKey] : null;
      return {
        key: s.yKey,
        name: s.yName,
        color: s.color || SEMANTIC_CHART_COLORS.subject,
        value: typeof v === 'number' ? v : null,
        formatter: (val: number | string) =>
          s.yKey === 'price' ? `$${Number(val).toFixed(2)}` : formatPrice(Number(val) * 1e6, 0),
      };
    });
  }, [series, chartData]);

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'date',
    lineSeries: series,
    showEndValueTags: true,
    xLabelFormatter: timeFormatter,
    xTitle: 'Revision date',
    yLabelFormatters: { cons: axisConsensus, price: axisPrice },
    yTitles: { cons: 'Consensus', price: 'Stock Price' },
    yAxisColors: { price: SEMANTIC_CHART_COLORS.benchmark },
    legendSeries,
    footnote: 'Source: InSync (consensus revisions)  ·  FMP (price)',
    emptyMessage: 'No data available',
  };

  const corr = displayData?.correlation;
  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="min-w-0 mr-1 flex-1">
        <h3 className="text-sm font-semibold text-neutral-900 truncate">Stock Price vs Consensus Revision</h3>
        <div className="text-[11px] text-neutral-400 truncate">
          <span className="uppercase tracking-wide">{ticker} · {kpi}{period ? ` · FY${period}` : ''}</span>
        </div>
      </div>
      <KeyMetricBadge label="Correlation" value={corr != null ? corr.toFixed(2) : '--'} />
      <KeyMetricBadge label="Latest" value={chartData.length ? formatPrice(chartData[chartData.length - 1].consensus * 1e6, 0) : '--'} />
      {internalEst != null && chartData.length > 0 && (() => {
        const street = chartData[chartData.length - 1].consensus;
        const delta = street ? (internalEst / street - 1) * 100 : null;
        const deltaLabel = delta == null ? '' : ` (${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs street)`;
        return <KeyMetricBadge label="Your Est" value={`${formatPrice(internalEst * 1e6, 0)}${deltaLabel}`} />;
      })()}
    </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      toolbar={header}
    />
  );
};

export default ConsensusRevisionWidgetRenderer;
